import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { m } from '../lib/motion.js';
import { api } from '../lib/api.js';
import { queryKeys } from '../lib/queryKeys.js';
import { useProducts, useBrands } from '../features/catalog/queries.js';
import { useAddToCart } from '../features/cart/useCart.js';
import { ProductCard } from '../components/commerce/ProductCard.jsx';
import { ProductRail } from '../components/commerce/ProductRail.jsx';
import { SpecStrip } from '../components/commerce/SpecStrip.jsx';
import { PriceTag } from '../components/commerce/PriceTag.jsx';
import { Rating } from '../components/commerce/Rating.jsx';
import { SkeletonCard } from '../components/ui/Skeleton.jsx';
import { EmptyState } from '../components/ui/EmptyState.jsx';
import { Button } from '../components/ui/Button.jsx';
import { Section, SectionHeading } from '../components/layout/Section.jsx';
import { useReducedMotion } from '../hooks/useReducedMotion.js';
import { imageProps, lqipStyle, formatDate, plainText, cn } from '../lib/format.js';
import { env } from '../lib/env.js';

/**
 * The home page — the twelve ordered sections of PRD §6.2.
 *
 * The hero is one orchestrated sequence: elements enter in reading order over
 * 400ms, once, on load. It is the only choreographed moment in the store;
 * everything below uses the single fade-and-rise on first scroll.
 *
 * Nothing here animates the LCP element's opacity from zero, because an element
 * that starts invisible is one the browser reports as painting late.
 */

const stagger = (index, reduced) => ({
  initial: reduced ? { opacity: 0 } : { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: {
    duration: reduced ? 0.2 : 0.4,
    delay: reduced ? 0 : index * 0.07,
    ease: [0.25, 1, 0.5, 1],
  },
});

/**
 * The same entrance without the fade, for the headline. On a narrow viewport the
 * `h1` is the largest contentful paint, and an element that starts at zero
 * opacity is one the browser reports as having painted whenever the animation
 * finishes rather than when the text actually landed. It rises; it never fades.
 */
const rise = (reduced) => ({
  initial: reduced ? false : { y: 14 },
  animate: { y: 0 },
  transition: { duration: reduced ? 0 : 0.4, delay: reduced ? 0 : 0.07, ease: [0.25, 1, 0.5, 1] },
});

export const Home = () => {
  const reduced = useReducedMotion();
  const addToCart = useAddToCart();

  const featured = useProducts({ featured: 'true', limit: 8 });
  const bestSellers = useProducts({ sort: 'popular', limit: 8 });
  const newArrivals = useProducts({ isNewArrival: 'true', limit: 8 });
  const sportHeroes = useProducts({ world: 'sport', sort: 'popular', limit: 3 });
  const { data: brands } = useBrands({ activeOnly: 'true' });

  const { data: journal } = useQuery({
    queryKey: queryKeys.blog.list({ limit: 3 }),
    queryFn: () => api.get('/blog', { params: { limit: 3 } }),
    staleTime: 10 * 60 * 1000,
  });

  const { data: reviewWall } = useQuery({
    queryKey: ['home', 'top-rated'],
    queryFn: () => api.get('/products', { params: { sort: 'rating', limit: 6 } }),
    staleTime: 10 * 60 * 1000,
  });

  const quickAdd = (product) =>
    product.type === 'simple' ? () => addToCart.mutate({ productId: product._id }) : undefined;

  const lead = journal?.[0];

  return (
    <>
      <Helmet>
        <title>Golden Eagle — fragrance and sports equipment</title>
        <meta
          name="description"
          content="Two collections under one masthead. Every specification on every page, so you can compare before you commit."
        />
        <link rel="canonical" href={env.siteUrl} />
        <meta property="og:title" content="Golden Eagle — fragrance and sports equipment" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Golden Eagle',
            url: env.siteUrl,
            description: 'Fragrance and sports equipment, chosen carefully and sold plainly.',
          })}
        </script>
      </Helmet>

      {/* 1 ─────────────────────────────────────────────────────────── hero */}
      <Hero products={featured.data} loading={featured.isLoading} reduced={reduced} />


      {/* 2 ──────────────────────────────────────────────── dual-world split */}
      <section aria-label="Choose a collection" className="border-b border-mist">
        <div className="mx-auto grid max-w-(--container-content) gap-px bg-mist sm:grid-cols-2">
          <WorldTile
            world="fragrance"
            title="Fragrance"
            line="Twenty compositions. Concentration, notes, and longevity on every one."
            to="/shop?world=fragrance"
          />
          <WorldTile
            world="sport"
            title="Sport"
            line="Rackets, weights, and kit. Material, weight, and level on every one."
            to="/shop?world=sport"
          />
        </div>
      </section>

      {/* 3 ─────────────────────────────────────────── featured collection */}
      <Section>
        <SectionHeading
          eyebrow="Chosen by us"
          title="Featured"
          description="What we would put in your hand if you walked in."
          action={{ label: 'See everything', to: '/shop' }}
        />
        <ProductRail products={featured.data} isPending={featured.isPending} />
      </Section>

      {/* 4 ─────────────────────────────────────────────────── best sellers */}
      <Section className="border-t border-mist">
        <SectionHeading
          eyebrow="What people buy"
          title="Best sellers"
          action={{ label: 'See everything', to: '/shop?sort=popular' }}
        />

        {bestSellers.isPending ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-9 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        ) : bestSellers.data?.length > 0 ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-9 lg:grid-cols-4">
            {bestSellers.data.slice(0, 8).map((product, index) => (
              <ProductCard
                key={product._id}
                product={product}
                priority={index < 4}
                onQuickAdd={quickAdd(product)}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            title="The shelves are still being filled"
            description="Nothing is published yet. Once products go live they appear here, ordered by what actually sells."
            action={{ label: 'Browse everything', to: '/shop' }}
          />
        )}
      </Section>

      {/* 5 ────────────────────────────────────── editorial band — The Note */}
      {lead && (
        <Section data-world="fragrance" className="border-t border-mist bg-surface" bleed>
          <div className="mx-auto max-w-(--container-content) px-4 sm:px-6">
            <div className="grid gap-8 lg:grid-cols-2 lg:items-center lg:gap-16">
              {lead.featuredImage && (
                <Link to={`/journal/${lead.slug}`} className="block overflow-hidden">
                  <img
                    {...imageProps(
                      { url: lead.featuredImage, alt: lead.featuredImageAlt || lead.title },
                      { sizes: '(min-width: 1024px) 46vw, 100vw' },
                    )}
                    className="aspect-4/3 w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                  />
                </Link>
              )}

              <div>
                <p className="font-mono text-2xs tracking-[0.16em] text-accent uppercase">
                  {lead.categories?.[0]?.name ?? 'The Note'}
                </p>
                <h2 className="mt-3 font-display text-2xl leading-tight tracking-[-0.02em] text-ink">
                  <Link to={`/journal/${lead.slug}`} className="hover:text-accent">
                    {lead.title}
                  </Link>
                </h2>
                <p className="mt-3 max-w-prose text-sm leading-relaxed text-muted">{lead.excerpt}</p>
                <p className="mt-4 font-mono text-2xs text-muted">
                  {formatDate(lead.publishedAt)} · {lead.readTimeMinutes} min read
                </p>
                <Button to={`/journal/${lead.slug}`} variant="outline" className="mt-6">
                  Read it
                </Button>
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* 6 ──────────────────────────────── sports performance comparison */}
      {sportHeroes.data?.length >= 2 && (
        <Section data-world="sport" className="border-t border-mist">
          <SectionHeading
            eyebrow="Compare"
            title="Three that do different jobs"
            description="The same three figures on each, so the difference is the thing you read rather than the marketing."
            action={{ label: 'All sport', to: '/shop?world=sport' }}
          />

          <div className="grid gap-px bg-mist md:grid-cols-3">
            {sportHeroes.data.slice(0, 3).map((product) => (
              <Link
                key={product._id}
                to={`/product/${product.slug}`}
                className="group flex flex-col bg-surface p-6 transition-colors hover:bg-accent-soft focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
              >
                {product.images?.[0] && (
                  <img
                    {...imageProps(product.images[0], { sizes: '(min-width: 768px) 30vw, 100vw' })}
                    alt={product.images[0].alt || product.title}
                    className="aspect-4/3 w-full object-cover"
                  />
                )}
                <SpecStrip items={product.specStrip} className="mt-5" />
                <h3 className="mt-2 text-base text-ink group-hover:text-accent">{product.title}</h3>
                <div className="mt-auto pt-4">
                  <PriceTag
                    price={product.type === 'variable' ? product.priceRange?.min : product.price}
                    priceRange={product.priceRange}
                    isVariable={product.type === 'variable'}
                  />
                  {product.ratingCount > 0 && (
                    <Rating value={product.ratingAvg} count={product.ratingCount} className="mt-2" />
                  )}
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* 7 ───────────────────────────────────────────────── new arrivals */}
      {newArrivals.data?.length > 0 && (
        <Section className="border-t border-mist">
          <SectionHeading
            eyebrow="Just landed"
            title="New arrivals"
            action={{ label: 'See all new', to: '/shop?isNewArrival=true' }}
          />
          <ProductRail products={newArrivals.data} isPending={newArrivals.isPending} />
        </Section>
      )}

      {/* 8 ────────────────────────────────────────────────────── trust row */}
      <section className="border-y border-mist bg-surface">
        <div className="mx-auto grid max-w-(--container-content) gap-px bg-mist sm:grid-cols-2 lg:grid-cols-4">
          <TrustItem title="Free over ₹1,999" line="Anywhere in India. One to two days in Bengaluru." />
          <TrustItem title="Seven-day returns" line="Unused and sealed. Faulty items any time." />
          <TrustItem
            title="Bought from the brand"
            line="Or its authorised Indian distributor. Never a grey import."
          />
          <TrustItem title="Razorpay checkout" line="We never see your card. UPI, cards, netbanking." />
        </div>
      </section>

      {/* 9 ──────────────────────────────────────────────── reviews wall */}
      {reviewWall?.length > 0 && (
        <Section>
          <SectionHeading
            eyebrow="In their words"
            title="What buyers rate highest"
            description="Every review is read before it appears, and the critical ones stay up."
          />
          <div className="grid gap-px bg-mist sm:grid-cols-2 lg:grid-cols-3">
            {reviewWall.slice(0, 6).map((product) => (
              <Link
                key={product._id}
                to={`/product/${product.slug}#reviews`}
                className="bg-surface p-6 transition-colors hover:bg-paper focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
              >
                <Rating value={product.ratingAvg} count={product.ratingCount} size="lg" />
                <p className="mt-3 text-sm leading-relaxed text-ink">{product.shortDescription}</p>
                <p className="mt-4 font-mono text-2xs tracking-[0.08em] text-muted uppercase">
                  {product.title}
                </p>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* 10 ──────────────────────────────────────────────────── the brands */}
      {brands?.length > 0 && (
        <Section className="border-t border-mist">
          <SectionHeading
            eyebrow="Who makes it"
            title="The houses we stock"
            action={{ label: 'All brands', to: '/brands' }}
          />
          <ul className="grid grid-cols-2 gap-px bg-mist sm:grid-cols-3 lg:grid-cols-6">
            {brands.slice(0, 12).map((brand) => (
              <li key={brand._id}>
                <Link
                  to={`/brand/${brand.slug}`}
                  className="flex h-24 items-center justify-center bg-surface px-4 text-center text-xs text-muted transition-colors hover:bg-paper hover:text-ink focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
                >
                  {brand.name}
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* 11 ────────────────────────────────────────────────────── journal */}
      {journal?.length > 1 && (
        <Section className="border-t border-mist">
          <SectionHeading
            eyebrow="Reading"
            title="From the journal"
            action={{ label: 'All writing', to: '/journal' }}
          />
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {journal.slice(0, 3).map((post) => (
              <article key={post._id}>
                <Link to={`/journal/${post.slug}`} className="group block">
                  {post.featuredImage && (
                    <div className="overflow-hidden">
                      <img
                        {...imageProps(
                          { url: post.featuredImage, alt: post.featuredImageAlt || post.title },
                          { sizes: '(min-width: 1024px) 30vw, 100vw' },
                        )}
                        className="aspect-3/2 w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      />
                    </div>
                  )}
                  <p className="mt-4 font-mono text-2xs tracking-[0.1em] text-muted uppercase">
                    {post.categories?.[0]?.name} · {post.readTimeMinutes} min
                  </p>
                  <h3 className="mt-1.5 text-base leading-snug text-ink group-hover:text-accent">
                    {post.title}
                  </h3>
                  <p className="mt-2 text-xs leading-relaxed text-muted">
                    {plainText(post.excerpt, 130)}
                  </p>
                </Link>
              </article>
            ))}
          </div>
        </Section>
      )}

      {/* 12 — the footer sitemap and newsletter capture live in Footer.jsx,
             which every page shares. */}
    </>
  );
};

/**
 * The hero.
 *
 * An editorial split: the argument on the left, the goods on the right. The
 * imagery is drawn from whatever is actually featured in the catalogue rather
 * than baked in, so the page an operator sees is the page they curated — and it
 * degrades to the type column alone when nothing is featured yet, which is a
 * legitimate state on a fresh install rather than a broken layout.
 *
 * The lead image is the LCP element on most viewports, so it loads eagerly at
 * high priority and — critically — never animates its opacity from zero. Only
 * the frame around it moves.
 */
const Hero = ({ products, loading, reduced }) => {
  const picks = (products ?? []).filter((product) => product.image?.url).slice(0, 3);
  const [lead, ...rest] = picks;

  return (
    <section className="relative overflow-hidden border-b border-mist bg-paper">
      {/* A hairline rule the type sits against. Decorative, drawn in tokens, and
          hidden from assistive tech. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 [background-image:linear-gradient(to_right,var(--color-mist)_1px,transparent_1px)] [background-size:calc(100%/6)_100%] opacity-40"
      />

      <div className="relative mx-auto grid max-w-(--container-content) gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.05fr_1fr] lg:items-center lg:gap-14 lg:py-24">
        <div>
          <m.p
            {...stagger(0, reduced)}
            className="flex items-center gap-2.5 font-mono text-2xs tracking-[0.16em] text-muted uppercase"
          >
            <span className="inline-block h-px w-8 bg-ink" aria-hidden="true" />
            Fragrance · Sport
          </m.p>

          <m.h1
            {...rise(reduced)}
            className="mt-6 font-display text-3xl leading-[1.02] tracking-[-0.025em] text-balance text-ink lg:text-[clamp(2.75rem,4.4vw,4rem)]"
          >
            Two collections.
            <br />
            <span className="text-muted">One house.</span>
          </m.h1>

          <m.p {...stagger(2, reduced)} className="mt-6 max-w-lg text-base leading-relaxed text-muted">
            A fragrance you have to smell and a racket you have to swing are bought the same way —
            by knowing what is in it. So the specification is on every page, in the same place.
          </m.p>

          <m.div {...stagger(3, reduced)} className="mt-9 flex flex-wrap gap-2">
            <Button to="/shop?world=fragrance" size="lg">
              Browse fragrance
            </Button>
            <Button to="/shop?world=sport" size="lg" variant="outline">
              Browse sport
            </Button>
          </m.div>

          <m.dl
            {...stagger(4, reduced)}
            className="mt-12 grid max-w-lg grid-cols-3 gap-px border-t border-mist bg-mist"
          >
            <HeroStat value="Free" label="Shipping over ₹2,000" />
            <HeroStat value="7 days" label="Returns, no questions" />
            <HeroStat value="100%" label="Authentic, sourced direct" />
          </m.dl>
        </div>

        {/* The collage. Never renders a hole: skeletons while loading, nothing
            at all if the catalogue has no featured imagery. */}
        {loading ? (
          <div className="hidden gap-3 lg:grid lg:grid-cols-[1.35fr_1fr]" aria-hidden="true">
            <div className="aspect-[3/4] animate-pulse bg-mist" />
            <div className="grid gap-3">
              <div className="aspect-square animate-pulse bg-mist" />
              <div className="aspect-square animate-pulse bg-mist" />
            </div>
          </div>
        ) : lead ? (
          <m.div
            initial={reduced ? false : { opacity: 1, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduced ? 0 : 0.5, delay: reduced ? 0 : 0.12, ease: [0.25, 1, 0.5, 1] }}
            className="grid grid-cols-[1.35fr_1fr] gap-3"
          >
            <HeroTile product={lead} eager priority className="aspect-[3/4]" />

            <div className="grid gap-3">
              {rest.map((product) => (
                <HeroTile key={product._id} product={product} className="aspect-square" />
              ))}
            </div>
          </m.div>
        ) : null}
      </div>
    </section>
  );
};

const HeroStat = ({ value, label }) => (
  <div className="bg-paper px-1 pt-4">
    <dt className="font-display text-lg text-ink">{value}</dt>
    <dd className="mt-1 text-2xs leading-snug text-muted">{label}</dd>
  </div>
);

const HeroTile = ({ product, className, eager = false, priority = false }) => (
  <Link
    to={`/product/${product.slug}`}
    data-world={product.world}
    className={cn(
      'group relative block overflow-hidden bg-shell focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
      className,
    )}
  >
    <img
      {...imageProps(product.image, {
        sizes: '(min-width: 1024px) 26vw, 0px',
        eager,
      })}
      alt={product.image?.alt || product.title}
      style={lqipStyle(product.image)}
      className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
    />

    {/* Only the lead tile carries a caption. Three captions would turn a
        composition into a list. */}
    {priority && (
      <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/72 to-transparent p-3.5 pt-10">
        <span className="block font-mono text-2xs tracking-[0.14em] text-white/70 uppercase">
          Featured
        </span>
        <span className="mt-1 block truncate text-xs font-medium text-white">{product.title}</span>
      </span>
    )}
  </Link>
);

const WorldTile = ({ world, title, line, to }) => (
  <Link
    to={to}
    data-world={world}
    className="group relative flex min-h-64 flex-col justify-end bg-surface p-8 transition-colors hover:bg-accent-soft focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent lg:min-h-80 lg:p-12"
  >
    <span className="font-mono text-2xs tracking-[0.16em] text-accent uppercase">{world}</span>
    <h2 className="mt-3 font-display text-2xl tracking-[-0.02em] text-ink">{title}</h2>
    <p className="mt-2 max-w-sm text-xs leading-relaxed text-muted">{line}</p>
    <span className="mt-5 inline-flex items-center gap-2 text-xs text-ink">
      Browse
      <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1">
        →
      </span>
    </span>
  </Link>
);

const TrustItem = ({ title, line }) => (
  <div className="bg-surface px-6 py-8">
    <h3 className="text-sm text-ink">{title}</h3>
    <p className="mt-1.5 text-xs leading-relaxed text-muted">{line}</p>
  </div>
);

export default Home;
